import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import CRMNav from "./CRMNav";
import UbicacionPicker from "./UbicacionPicker";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatFecha = (str) => {
  if (!str) return null;
  const d = new Date(str);
  if (isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const ESTADO_BADGE = {
  prospecto: { bg: "#EEF2FF", color: "#3730A3" },
  activa:    { bg: "#F0FDF4", color: "#166534" },
  inactiva:  { bg: "#f5f5f5", color: "#666"    },
};

const SEGMENTO_BADGE = {
  // valores nuevos (claves en minúscula para lookup case-insensitive)
  "boutique":        { bg: "#fdf4ff", color: "#7e22ce" },
  "pequeña (-1m)":   { bg: "#f0f9ff", color: "#0369a1" },
  "mediana (2-10m)": { bg: "#fefce8", color: "#854d0e" },
  "grande (+10m)":   { bg: "#f0fdf4", color: "#166534" },
  // valores legacy
  "pequeño":         { bg: "#f0f9ff", color: "#0369a1" },
  "mediano":         { bg: "#fefce8", color: "#854d0e" },
  "grande":          { bg: "#fdf4ff", color: "#7e22ce" },
};

const ESTADO_LABELS = ["Todas", "Prospecto", "Activa", "Inactiva"];

const EMPTY_FORM = {
  id:        "",
  nombre:    "",
  industria: "",
  web:       "",
  instagram: "",
  linkedin:  "",
  direccion: "",
  estado:    "prospecto",
  segmento:  "",
  notas:     "",
  // Ubicación granular
  pais:      "Argentina",
  provincia: "",
  ciudad:    "",
  calle:     "",
  numero:    "",
};

// ─── Modal Nueva / Editar Cuenta ──────────────────────────────────────────────
const CuentaModal = ({ initial, onClose, onSaved, segmentos, industrias }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleDelete = async () => {
    try {
      const fd = new FormData();
      fd.append('id', form.id);
      const res = await axios.post(`${BASE}/crm_cuentas_eliminar.php`, fd);
      if (res.data?.success) {
        toast.success("Cuenta eliminada.");
        onSaved();
        onClose();
      } else {
        toast.error(res.data?.mensaje || "No se pudo eliminar.");
        setConfirmDelete(false);
      }
    } catch {
      toast.error("Error de conexión.");
      setConfirmDelete(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      toast.error("El nombre es obligatorio.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      const res = await axios.post(`${BASE}/crm_cuentas_guardar.php`, fd);
      if (res.data && res.data.success) {
        toast.success(form.id ? "Cuenta actualizada." : "Cuenta creada.");
        onSaved();
        onClose();
        // No llamamos setLoading(false) acá — el modal ya se desmontó
      } else {
        toast.error(res.data?.message || "Error al guardar la cuenta.");
        setLoading(false);
      }
    } catch {
      toast.error("Error de conexión.");
      setLoading(false);
    }
  };

  const fieldStyle = {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #e0d6dc",
    borderRadius: 8,
    fontSize: 14,
    boxSizing: "border-box",
    marginBottom: 14,
    outline: "none",
    fontFamily: "inherit",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 32,
          width: "100%",
          maxWidth: 480,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          maxHeight: "90vh",
          overflowY: "auto",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#9f0059",
            marginBottom: 18,
            marginTop: 0,
          }}
        >
          {form.id ? "Editar cuenta" : "Nueva cuenta"}
        </h2>

        <form onSubmit={handleSubmit} noValidate>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
            Nombre *
          </label>
          <input
            style={fieldStyle}
            type="text"
            value={form.nombre}
            onChange={set("nombre")}
            placeholder="Nombre del cliente"
            required
          />

          <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
            Industria
          </label>
          <select
            style={fieldStyle}
            value={form.industria ?? ""}
            onChange={set("industria")}
          >
            <option value="">— Sin industria —</option>
            {(industrias || []).map((ind) => (
              <option key={ind.id} value={ind.nombre}>{ind.nombre}</option>
            ))}
          </select>

          <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
            Web
          </label>
          <input
            style={fieldStyle}
            type="text"
            value={form.web}
            onChange={set("web")}
            placeholder="https://empresa.com"
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 0 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
                Instagram
              </label>
              <input
                style={fieldStyle}
                type="text"
                value={form.instagram ?? ""}
                onChange={set("instagram")}
                placeholder="@empresa o URL"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
                LinkedIn empresa
              </label>
              <input
                style={fieldStyle}
                type="text"
                value={form.linkedin ?? ""}
                onChange={set("linkedin")}
                placeholder="linkedin.com/company/..."
              />
            </div>
          </div>

          <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
            Ubicación
          </label>
          <div style={{ marginBottom: 14 }}>
            <UbicacionPicker
              value={{
                pais: form.pais,
                provincia: form.provincia,
                ciudad: form.ciudad,
                calle: form.calle,
                numero: form.numero,
              }}
              onChange={(v) => setForm((f) => ({ ...f, ...v }))}
            />
          </div>

          <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
            Estado
          </label>
          <select
            style={fieldStyle}
            value={form.estado}
            onChange={set("estado")}
          >
            <option value="prospecto">Prospecto</option>
            <option value="activa">Activa</option>
            <option value="inactiva">Inactiva</option>
          </select>

          <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
            Segmento
          </label>
          <select
            style={fieldStyle}
            value={form.segmento ?? ""}
            onChange={set("segmento")}
          >
            <option value="">— Sin segmento —</option>
            {(segmentos || []).map((seg) => (
              <option key={seg.id} value={seg.nombre}>{seg.nombre}</option>
            ))}
          </select>

          <label style={{ fontSize: 12, fontWeight: 700, color: "#888", display: "block", marginBottom: 4 }}>
            Notas
          </label>
          <textarea
            style={{ ...fieldStyle, resize: "vertical", minHeight: 80 }}
            value={form.notas}
            onChange={set("notas")}
            placeholder="Observaciones adicionales…"
          />

          {/* Confirmación de eliminación */}
          {confirmDelete && (
            <div style={{
              background: "#fff5f5",
              border: "1.5px solid #fca5a5",
              borderRadius: 8,
              padding: "12px 16px",
              marginTop: 12,
              fontSize: 13,
              color: "#991b1b",
            }}>
              <p style={{ margin: "0 0 10px", fontWeight: 600 }}>
                ¿Seguro que querés eliminar esta cuenta? Esta acción no se puede deshacer.
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={handleDelete}
                  style={{
                    padding: "7px 16px", background: "#dc2626", color: "#fff",
                    border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  Sí, eliminar
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  style={{
                    padding: "7px 16px", background: "transparent", color: "#555",
                    border: "1.5px solid #ccc", borderRadius: 7, fontSize: 13, cursor: "pointer",
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              marginTop: 16,
            }}
          >
            {/* Botón eliminar — solo al editar */}
            {form.id ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={loading || confirmDelete}
                style={{
                  padding: "9px 16px",
                  background: "transparent",
                  border: "1.5px solid #dc2626",
                  borderRadius: 8,
                  fontSize: 13,
                  cursor: "pointer",
                  color: "#dc2626",
                  fontWeight: 600,
                }}
              >
                Eliminar cuenta
              </button>
            ) : <span />}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                style={{
                  padding: "9px 20px",
                  background: "transparent",
                  border: "1.5px solid #ccc",
                  borderRadius: 8,
                  fontSize: 14,
                  cursor: "pointer",
                  color: "#555",
                }}
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                style={{
                  padding: "9px 20px",
                  background: "#9f0059",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  color: "#fff",
                  opacity: loading ? 0.7 : 1,
                }}
                disabled={loading}
              >
                {loading ? "Guardando…" : form.id ? "Guardar cambios" : "Crear cuenta"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const CRMCuentas = () => {
  const history = useHistory();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("Todas");
  const [showModal, setShowModal] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [segmentos, setSegmentos] = useState([]);
  const [industrias, setIndustrias] = useState([]);

  const fetchAccounts = useCallback(() => {
    setLoading(true);
    axios
      .post(`${BASE}/crm_cuentas_listar.php`)
      .then((res) => {
        setAccounts(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        toast.error("Error al cargar los clientes.");
        setAccounts([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAccounts();
    axios.get(`${BASE}/buscar_segmentos.php`).then((r) => setSegmentos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${BASE}/buscar_industrias.php`).then((r) => setIndustrias(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [fetchAccounts]);

  // ── Filtrado client-side ────────────────────────────────────────────────
  const filtered = accounts.filter((a) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      (a.nombre && a.nombre.toLowerCase().includes(q)) ||
      (a.industria && a.industria.toLowerCase().includes(q));

    const matchesFilter =
      activeFilter === "Todas" ||
      (a.estado && a.estado.toLowerCase() === activeFilter.toLowerCase());

    return matchesSearch && matchesFilter;
  });

  const openNewModal = () => {
    setEditAccount(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditAccount(null);
  };

  return (
    <section
      style={{
        minHeight: "100vh",
        background: "#f7f4f6",
        padding: "32px 16px",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <CRMNav />
      </div>

      {/* Header */}
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto 24px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#121212", margin: 0 }}>
            Clientes CRM
          </h2>
          <p style={{ fontSize: 13, color: "#888", marginTop: 3, marginBottom: 0 }}>
            {filtered.length} cliente{filtered.length !== 1 ? "s" : ""}
            {activeFilter !== "Todas" ? ` · ${activeFilter.toLowerCase()}s` : ""}
          </p>
        </div>
        <button
          style={{
            background: "#9f0059",
            color: "#fff",
            border: "none",
            borderRadius: 9,
            padding: "10px 22px",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          onClick={openNewModal}
        >
          + Nueva cuenta
        </button>
      </div>

      {/* Búsqueda */}
      <div style={{ maxWidth: 1100, margin: "0 auto 8px" }}>
        <input
          type="text"
          placeholder="Buscar por nombre o industria…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "9px 14px",
            border: "1.5px solid #e0d6dc",
            borderRadius: 8,
            fontSize: 14,
            outline: "none",
            width: 260,
            boxSizing: "border-box",
            background: "#fff",
          }}
        />
      </div>

      {/* Filter pills */}
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "flex",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
          paddingTop: 10,
        }}
      >
        {ESTADO_LABELS.map((lbl) => {
          const active = activeFilter === lbl;
          return (
            <button
              key={lbl}
              onClick={() => setActiveFilter(lbl)}
              style={{
                padding: "7px 16px",
                borderRadius: 20,
                border: active ? "1.5px solid #9f0059" : "1.5px solid #c990b0",
                background: active ? "#9f0059" : "#fff",
                color: active ? "#fff" : "#9f0059",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {lbl}
            </button>
          );
        })}
      </div>

      {/* Tabla */}
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 2px 24px rgba(159,0,89,0.07)",
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#9f0059" }}>
              {["Nombre", "Industria", "Segmento", "Estado", "Último contacto", "Contactos", "Acciones"].map(
                (col, ci) => (
                  <th
                    key={col}
                    style={{
                      background: "#9f0059",
                      color: "#fff",
                      padding: "13px 16px",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      textAlign: ci >= 5 ? "center" : "left",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", padding: "40px 0", color: "#aaa", fontSize: 14 }}
                >
                  Cargando clientes…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", padding: "40px 0", color: "#aaa", fontSize: 14 }}
                >
                  No se encontraron clientes.
                </td>
              </tr>
            ) : (
              filtered.map((row, i) => {
                const fechaFmt = formatFecha(row.ultima_interaccion);
                const estadoKey = (row.estado || "").toLowerCase();
                const badge = ESTADO_BADGE[estadoKey] || ESTADO_BADGE.inactiva;

                return (
                  <tr
                    key={row.id}
                    style={{ background: i % 2 === 0 ? "#fff" : "#fdf8fb" }}
                  >
                    <td
                      style={{
                        padding: "13px 16px",
                        fontSize: 13,
                        color: "#121212",
                        borderBottom: "1px solid #f5f0f3",
                        fontWeight: 600,
                      }}
                    >
                      {row.nombre}
                    </td>
                    <td
                      style={{
                        padding: "13px 16px",
                        fontSize: 13,
                        color: "#555",
                        borderBottom: "1px solid #f5f0f3",
                      }}
                    >
                      {row.industria || <span style={{ color: "#ccc" }}>—</span>}
                    </td>
                    <td
                      style={{
                        padding: "13px 16px",
                        fontSize: 13,
                        color: "#222",
                        borderBottom: "1px solid #f5f0f3",
                      }}
                    >
                      {row.segmento ? (() => {
                        const sb = SEGMENTO_BADGE[(row.segmento || "").toLowerCase()] || { bg: "#f3f4f6", color: "#555" };
                        return (
                          <span style={{
                            display: "inline-block", padding: "3px 10px", borderRadius: 20,
                            fontSize: 12, fontWeight: 700, background: sb.bg, color: sb.color,
                            textTransform: "capitalize",
                          }}>
                            {row.segmento}
                          </span>
                        );
                      })() : <span style={{ color: "#ccc" }}>—</span>}
                    </td>
                    <td
                      style={{
                        padding: "13px 16px",
                        fontSize: 13,
                        color: "#222",
                        borderBottom: "1px solid #f5f0f3",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 700,
                          background: badge.bg,
                          color: badge.color,
                          textTransform: "capitalize",
                        }}
                      >
                        {row.estado || "—"}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "13px 16px",
                        fontSize: 13,
                        color: "#222",
                        borderBottom: "1px solid #f5f0f3",
                      }}
                    >
                      {fechaFmt ? (
                        fechaFmt
                      ) : (
                        <span style={{ color: "#bbb" }}>Sin contacto</span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "13px 16px",
                        fontSize: 13,
                        color: "#222",
                        borderBottom: "1px solid #f5f0f3",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          minWidth: 28,
                          padding: "2px 10px",
                          borderRadius: 20,
                          background: "#f7f4f6",
                          color: "#9f0059",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {row.total_contactos ?? 0}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "13px 16px",
                        fontSize: 13,
                        color: "#222",
                        borderBottom: "1px solid #f5f0f3",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <button
                          style={{
                            background: "#fff",
                            color: "#9f0059",
                            border: "1.5px solid #9f0059",
                            borderRadius: 7,
                            padding: "6px 14px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setEditAccount(row);
                            setShowModal(true);
                          }}
                        >
                          Editar
                        </button>
                        <button
                          style={{
                            background: "#9f0059",
                            color: "#fff",
                            border: "none",
                            borderRadius: 7,
                            padding: "6px 14px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            history.push(`/dashboard/crm/cuenta/${row.id}`)
                          }
                        >
                          Ver detalle
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal nueva / editar cuenta */}
      {showModal && (
        <CuentaModal
          initial={editAccount || EMPTY_FORM}
          onClose={closeModal}
          onSaved={fetchAccounts}
          segmentos={segmentos}
          industrias={industrias}
        />
      )}
    </section>
  );
};

export default CRMCuentas;
